import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

const DEFAULT_BRAND_COLOR = "#012E88";

function applyBrandColor(hex) {
  if (typeof document === "undefined") return;
  const value = (hex && /^#[0-9a-fA-F]{6}$/.test(hex.trim())) ? hex.trim() : DEFAULT_BRAND_COLOR;
  document.documentElement.style.setProperty("--brand-primary", value);
}

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = user
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);

  const refreshOrg = useCallback(async () => {
    try {
      const { data } = await api.get("/orgs/current");
      setOrg(data);
      applyBrandColor(data?.brand_primary_color);
      return data;
    } catch (_e) {
      setOrg(null);
      applyBrandColor(null);
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      setUser(false);
      setOrg(null);
      applyBrandColor(null);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, AuthCallback handles session exchange first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Load the current org's brand as soon as we have an authenticated user, so
  // the theme colour applies globally (headers, buttons, panels, focus rings).
  useEffect(() => {
    if (user && user.id && user.org_id) {
      refreshOrg();
    }
  }, [user, refreshOrg]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    setUser(false);
    setOrg(null);
    applyBrandColor(null);
  }, []);

  const value = { user, setUser, org, setOrg, refreshOrg, checkAuth, logout };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
